"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "./auth-provider";
import { getPlayerProfile } from "@/services/player/get-player-profile";
import { updatePlayerProfile } from "@/services/player/update-player-profile";
import { listPokeTeams } from "@/services/poketeam/list-poketeams";
import { getPlayerQueueStatus } from "@/services/duel/get-player-queue-status";
import { getCurrentRaid } from "@/services/raid/get-current-raid";
import { getCountryByLanguageCode } from "@/data/countries";
import { PlayerProfileOutput } from "types/web-app";
import { Pokemon } from "prisma-layer";

import { DailyRewardDialog } from "@/components/ui/dialog/daily-reward-dialog";

type UserState =
  | { status: "idle" | "loading" }
  | { status: "loaded"; profile: PlayerProfileOutput }
  | { status: "not_registered" }
  | { status: "awaiting_alpha_approval" }
  | { status: "onboarding_in_progress" }
  | { status: "error"; error: string };

type UserContextValue = {
  state: UserState;
  reloadProfile: () => Promise<void>;
  updateProfile: (updates: { country?: string }) => Promise<void>;
  pokeCoins: number;
  setPokeCoins: (coins: number | ((prev: number) => number)) => void;
  pokeShards: number | null;
  setPokeShards: (
    shards: number | null | ((prev: number | null) => number | null),
  ) => void;
  rewardedDuelsToday: number;
  playerCountry: string | null | undefined;
  playerElo: number | null;
  username: string | undefined;
  mainTeamId: number | undefined;
  mainTeamPokemons: Array<
    Pick<Pokemon, "id" | "display_id" | "base_pokemon_name" | "level">
  >;
  duelStatus: {
    status: "IDLE" | "WAITING" | "MATCHED" | "IN_DUEL";
    duelId: string | null;
  };
  raidStatus: {
    raidId: string | null;
    status: string | null;
  };
};

const UserContext = createContext<UserContextValue | null>(null);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { getAccessToken, state: authState } = useAuth();
  const [userState, setUserState] = useState<UserState>({ status: "idle" });
  const [pokeCoins, setPokeCoins] = useState<number>(0);
  const [pokeShards, setPokeShards] = useState<number | null>(null);
  const [rewardedDuelsToday, setRewardedDuelsToday] = useState<number>(0);
  const [playerCountry, setPlayerCountry] = useState<string | null | undefined>(
    undefined,
  );
  const [mainTeamId, setMainTeamId] = useState<number | undefined>(undefined);
  const [mainTeamPokemons, setMainTeamPokemons] = useState<
    Array<Pick<Pokemon, "id" | "display_id" | "base_pokemon_name" | "level">>
  >([]);
  const [duelStatus, setDuelStatus] = useState<UserContextValue["duelStatus"]>({
    status: "IDLE",
    duelId: null,
  });
  const [raidStatus, setRaidStatus] = useState<UserContextValue["raidStatus"]>({
    raidId: null,
    status: null,
  });
  const [isDailyRewardOpen, setIsDailyRewardOpen] = useState(false);

  /**
   * Fetches player profile and handles country auto-detection
   */
  const fetchPlayerProfile = async () => {
    const accessToken = getAccessToken();
    if (!accessToken) {
      setPokeCoins(0);
      setPokeShards(0);
      setRewardedDuelsToday(0);
      setUserState({ status: "error", error: "Not authenticated" });
      return;
    }

    try {
      // Only show loading state if we don't already have a loaded profile
      // This prevents UI flicker during background token refreshes
      if (userState.status !== "loaded") {
        setUserState({ status: "loading" });
      }

      const profileResponse = await getPlayerProfile(accessToken);

      // Handle not registered (assuming 404 or !ok with specific message)
      if (!profileResponse.ok) {
        if (
          profileResponse.statusCode === 404 ||
          profileResponse.message?.includes("not found") ||
          profileResponse.message?.includes("not registered")
        ) {
          setUserState({ status: "not_registered" });
          return;
        }
        throw new Error(profileResponse.message || "Failed to fetch profile");
      }

      const profile = profileResponse.data;

      if (!profile) {
        setUserState({ status: "not_registered" });
        return;
      }

      if (!profile.alpha_permission) {
        setUserState({ status: "awaiting_alpha_approval" });
        return;
      }

      if (!profile.onboarding_completed) {
        setUserState({ status: "onboarding_in_progress" });
        return;
      }

      // Update state with profile data
      setPokeCoins(profile.poke_coins ?? 0);
      setPokeShards(profile.poke_shards ?? 0);
      setRewardedDuelsToday(profile.stats?.rewarded_duels_today ?? 0);
      setPlayerCountry(profile.country);

      // Fetch all necessary data for the context in parallel
      try {
        const [teamsRes, queueRes, raidRes] = await Promise.allSettled([
          listPokeTeams(accessToken),
          getPlayerQueueStatus(accessToken),
          getCurrentRaid(accessToken),
        ]);

        if (teamsRes.status === "fulfilled" && teamsRes.value.ok) {
          const teams = teamsRes.value.data.teams;
          const mainTeam = teams.find((team) => team.isMainTeam) || teams[0];
          if (mainTeam) {
            setMainTeamId(mainTeam.id);
            setMainTeamPokemons(mainTeam.pokemons);
          } else {
            setMainTeamId(undefined);
            setMainTeamPokemons([]);
          }
        }

        if (queueRes.status === "fulfilled" && queueRes.value.ok) {
          setDuelStatus({
            status: queueRes.value.data.status,
            duelId: queueRes.value.data.queue_entry?.duel_id ?? null,
          });
        }

        if (raidRes.status === "fulfilled" && raidRes.value.ok) {
          setRaidStatus({
            raidId: raidRes.value.data.raid_id || null,
            status: raidRes.value.data.raid_status || null,
          });
        }
      } catch (error) {
        console.error(
          "Failed to fetch additional data during profile load:",
          error,
        );
      }

      setUserState({ status: "loaded", profile });

      // Check for daily reward
      const lastClaim = profile.last_daily_reward_claim
        ? new Date(profile.last_daily_reward_claim)
        : null;
      const today = new Date();
      today.setUTCHours(0, 0, 0, 0);

      if (!lastClaim || lastClaim < today) {
        setIsDailyRewardOpen(true);
      }

      // Auto-detect country if not set
      if (!profile.country) {
        await autoDetectCountry(accessToken, profile);
      }
    } catch (error) {
      console.error("Failed to fetch player profile:", error);
      setPokeCoins(0);
      setPokeShards(0);
      setRewardedDuelsToday(0);
      setUserState({
        status: "error",
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  };

  /**
   * Auto-detects country using Telegram language code or browser language
   */
  const autoDetectCountry = async (
    accessToken: string,
    profile: PlayerProfileOutput,
  ) => {
    try {
      // Try Telegram language code first
      const telegramLang =
        authState.status === "authenticated"
          ? authState.user.language_code
          : undefined;
      let detectedCountry = getCountryByLanguageCode(telegramLang || "");

      // Fallback to browser language
      if (!detectedCountry) {
        detectedCountry = getCountryByLanguageCode(navigator.language);
      }

      if (detectedCountry) {
        // Auto-update profile
        try {
          const updateRes = await updatePlayerProfile(accessToken, {
            country: detectedCountry,
          });
          if (updateRes.ok && updateRes.data?.data?.country) {
            setPlayerCountry(updateRes.data.data.country);
            // Update the profile in state as well
            if (userState.status === "loaded") {
              setUserState({
                status: "loaded",
                profile: {
                  ...userState.profile,
                  country: updateRes.data.data.country,
                },
              });
            }
          }
        } catch (e) {
          console.error("Failed to auto-update country:", e);
        }
      }
    } catch (error) {
      console.error("Failed to auto-detect country:", error);
    }
  };

  /**
   * Updates player profile and refreshes local state
   */
  const updateProfile = async (updates: { country?: string }) => {
    const accessToken = getAccessToken();
    if (!accessToken) {
      throw new Error("Not authenticated");
    }

    try {
      const updateRes = await updatePlayerProfile(accessToken, updates);
      if (updateRes.ok && updateRes.data?.data) {
        const updatedProfile = updateRes.data.data;
        setPokeCoins(updatedProfile.poke_coins ?? 0);
        setPokeShards(updatedProfile.poke_shards ?? 0);
        setPlayerCountry(updatedProfile.country);
        setUserState({ status: "loaded", profile: updatedProfile });
      } else {
        throw new Error(updateRes.message || "Failed to update profile");
      }
    } catch (error) {
      console.error("Failed to update profile:", error);
      throw error;
    }
  };

  // Fetch profile when auth state changes to authenticated
  useEffect(() => {
    if (authState.status === "authenticated") {
      fetchPlayerProfile();
    } else if (authState.status === "idle" || authState.status === "loading") {
      setUserState({ status: "idle" });
    } else {
      setUserState({ status: "error", error: "Not authenticated" });
    }
  }, [
    authState.status,
    authState.status === "authenticated"
      ? authState.user.language_code
      : undefined,
  ]);

  const value = useMemo<UserContextValue>(
    () => ({
      state: userState,
      reloadProfile: fetchPlayerProfile,
      updateProfile,
      pokeCoins,
      setPokeCoins,
      pokeShards,
      setPokeShards,
      rewardedDuelsToday,
      playerCountry,
      mainTeamId,
      mainTeamPokemons,
      duelStatus,
      raidStatus,
      playerElo:
        userState.status === "loaded"
          ? userState.profile.stats?.elo ?? null
          : null,
      username:
        userState.status === "loaded"
          ? userState.profile.username
          : authState.status === "authenticated"
          ? authState.user.username
          : undefined,
    }),
    [
      userState,
      pokeCoins,
      pokeShards,
      rewardedDuelsToday,
      playerCountry,
      mainTeamId,
      mainTeamPokemons,
      duelStatus,
      raidStatus,
      authState.status,
      authState.status === "authenticated"
        ? authState.user.username
        : undefined,
    ],
  );

  return (
    <UserContext.Provider value={value}>
      {children}
      <DailyRewardDialog
        isOpen={isDailyRewardOpen}
        onClose={() => setIsDailyRewardOpen(false)}
      />
    </UserContext.Provider>
  );
};

/**
 * Hook to access user state and methods.
 */
export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUser must be used within <UserProvider>");
  return ctx;
}
